import { index, integer, sqliteTable, text, uniqueIndex } from 'drizzle-orm/sqlite-core'

import { timestamps } from './_columns'
import { sites } from './sites'

/**
 * A site's subscription: the console is the source of truth. Staff set the monthly price and the dates by hand when a
 * client agrees; an approved payment moves `paidUntil`. Pushed to the site on every change and every sync.
 */
export const siteSubscriptions = sqliteTable('site_subscriptions', {
  siteId: text('site_id')
    .primaryKey()
    .references(() => sites.id, { onDelete: 'cascade' }),
  /** No subscription needed (demo sites, Digimasi's own CMS). */
  exempt: integer('exempt', { mode: 'boolean' }).notNull().default(false),
  /** Rupiah per month. */
  monthlyPrice: integer('monthly_price').notNull().default(0),
  startedAt: text('started_at'),
  /** Last paid day, inclusive (`YYYY-MM-DD`). Null: not set up, the site runs as before. */
  paidUntil: text('paid_until'),
  ...timestamps,
})

/**
 * Renewal payments sent from the sites' CMSs (the console's copy and the queue). `id` is the site's own payment id.
 * The proof stays in the site's storage; the console fetches it for the preview and keeps its SHA-256.
 */
export const subscriptionPayments = sqliteTable(
  'subscription_payments',
  {
    id: text('id').primaryKey(),
    siteId: text('site_id')
      .notNull()
      .references(() => sites.id, { onDelete: 'cascade' }),
    package: text('package').notNull(),
    months: integer('months').notNull(),
    monthlyPrice: integer('monthly_price').notNull(),
    amount: integer('amount').notNull(),
    /** `pending | approved | rejected`. */
    status: text('status').notNull().default('pending'),
    reason: text('reason').notNull().default(''),
    /** Where the proof is in the site's storage (R2), for the compliance log. */
    proofKey: text('proof_key').notNull(),
    proofName: text('proof_name').notNull(),
    proofType: text('proof_type').notNull(),
    proofSize: integer('proof_size').notNull(),
    proofSha256: text('proof_sha256').notNull(),
    submittedBy: text('submitted_by').notNull(),
    /** When the client sent it (the site's clock). */
    submittedAt: text('submitted_at').notNull(),
    decidedBy: text('decided_by'),
    decidedAt: text('decided_at'),
    receiptNo: text('receipt_no'),
    periodFrom: text('period_from'),
    periodTo: text('period_to'),
    /** The site has the decision (it is resent on every sync until then). */
    siteSynced: integer('site_synced', { mode: 'boolean' }).notNull().default(false),
    ...timestamps,
  },
  (t) => [index('ix_subscription_payments_status').on(t.status, t.submittedAt), index('ix_subscription_payments_site').on(t.siteId, t.submittedAt), uniqueIndex('ux_subscription_payments_receipt').on(t.receiptNo)],
)

/** Receipt numbers: one running sequence per month (`DGM/KW/<yyyy>/<mm>/<nnn>`). */
export const receiptCounters = sqliteTable('receipt_counters', {
  /** `YYYY-MM`. */
  period: text('period').primaryKey(),
  last: integer('last').notNull().default(0),
})

/**
 * The compliance log: every approval, rejection and manual change of a subscription. Rows are only ever added: no
 * foreign keys (a removed site or staff member keeps its lines), and nothing in the code updates or deletes them.
 */
export const subscriptionLog = sqliteTable(
  'subscription_log',
  {
    id: text('id').primaryKey(),
    /** `approve | reject | manual`. */
    action: text('action').notNull(),
    staffId: text('staff_id'),
    staffName: text('staff_name').notNull(),
    siteId: text('site_id').notNull(),
    siteSlug: text('site_slug').notNull(),
    paymentId: text('payment_id'),
    months: integer('months'),
    amount: integer('amount'),
    oldPaidUntil: text('old_paid_until'),
    newPaidUntil: text('new_paid_until'),
    proofKey: text('proof_key'),
    proofSha256: text('proof_sha256'),
    receiptNo: text('receipt_no'),
    /** Rejection reason, or what a manual change changed. */
    detail: text('detail').notNull().default(''),
    createdAt: timestamps.createdAt,
  },
  (t) => [index('ix_subscription_log_created_at').on(t.createdAt), index('ix_subscription_log_site').on(t.siteId, t.createdAt)],
)

export type SiteSubscriptionRow = typeof siteSubscriptions.$inferSelect
export type ConsolePaymentRow = typeof subscriptionPayments.$inferSelect
export type SubscriptionLogRow = typeof subscriptionLog.$inferSelect
